import { formatAmount, USD } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { StatusBadge } from "../components/badges";
import { ErrorAlert } from "../components/ErrorAlert";
import { UnsavedDraftDot } from "../components/UnsavedDraftDot";
import { MAX_BOX_SLUG_LENGTH, matchesSearch } from "../lib/boxes";
import { formatDate, formatNumber } from "../lib/format";
import { slugify } from "../lib/slug";
import { useTRPC } from "../lib/trpc";

export function BoxTemplatesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  // Unpaginated and small, so one in-memory filter beats a round-trip — the same
  // reasoning the tenant Boxes page applies to this list.
  const templates = useQuery(trpc.platform.boxTemplates.list.queryOptions());
  const term = search.trim();
  const rows = (templates.data ?? []).filter((box) => matchesSearch(box, term));

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const create = useMutation(
    trpc.platform.boxTemplates.create.mutationOptions({
      onSuccess: (box) => {
        void queryClient.invalidateQueries({ queryKey: trpc.platform.boxTemplates.list.pathKey() });
        setShowCreate(false);
        void navigate({ to: "/templates/$boxId", params: { boxId: box.id } });
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Box templates</h1>
          <p className="text-sm text-muted-foreground">
            Boxes authored by Loot OS and shared across every tenant. Editing one here changes it
            for every environment that enabled it; enabling is per environment, on each tenant's
            Boxes page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear
            </Button>
          )}
          <Input
            className="w-64"
            type="search"
            placeholder="Search name, slug or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            type="button"
            onClick={() => {
              create.reset();
              setShowCreate(true);
            }}
          >
            <Plus />
            New box
          </Button>
        </div>
      </div>

      {templates.error && <ErrorAlert message={templates.error.message} />}

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Version</TableHead>
                {/* No tenant in scope: box templates are priced in cents for every tenant and each
                    storefront renders them in its own currency, so here they read as dollars. */}
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Enabled</TableHead>
                <TableHead className="text-right">Opened</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                    {term
                      ? `No box templates match “${term}”.`
                      : "No box templates yet. Create one, define its drop table and publish."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((box) => (
                <TableRow key={box.id}>
                  <TableCell>
                    {box.imageUrl ? (
                      // max-w-none: preflight's `max-width: 100%` would otherwise let the cell squeeze
                      // the width while the height stays fixed, so the thumbnail stopped being square.
                      <img
                        className="size-8 max-w-none rounded-md border object-cover"
                        src={box.imageUrl}
                        alt=""
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/templates/$boxId"
                      params={{ boxId: box.id }}
                      className="font-medium hover:underline"
                    >
                      {box.name}
                    </Link>
                    <UnsavedDraftDot boxId={box.id} />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {box.slug}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={box.status} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {box.currentVersion || "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {box.priceCents ? formatAmount(box.priceCents, USD) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {box.enabledCount ? `${formatNumber(box.enabledCount)} env` : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatNumber(box.openedCount)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(box.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New box template</DialogTitle>
            <DialogDescription>
              Give the box a name and a URL-safe slug — the slug is its identity for every tenant
              and can't be changed later. You'll define its drop table next.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ name, slug, imageUrl });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="box-name">Name</Label>
              <Input
                id="box-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value, MAX_BOX_SLUG_LENGTH));
                }}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="box-slug">Slug</Label>
              <Input
                id="box-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                pattern="[a-z0-9][a-z0-9\-]*"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="box-image-url">Image URL (optional)</Label>
              <Input
                id="box-image-url"
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create box"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
